import type { FormatoDownload, PacoteDosAutos, ParteDoPacote } from '../types/baixarProcesso.types';

/** Limite por anexo mais comum nos sistemas de tribunal — é o corte das partes do ZIP. */
export const LIMITE_POR_PARTE_MB = 5;

const BYTES_POR_MB = 1024 * 1024;

const formatadorUmaCasa = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

export function formatarBytes(bytes: number): string {
  return `${formatadorUmaCasa.format(bytes / BYTES_POR_MB)} MB`;
}

export function nomeDoPdf(numeroProcesso: string): string {
  return `${numeroProcesso}-autos.pdf`;
}

/** `02-de-05-ARB-1.pdf`: o zero à esquerda mantém a ordem das partes em qualquer pasta. */
export function nomeDaParte(numeroProcesso: string, numero: number, total: number): string {
  const dois = (valor: number) => String(valor).padStart(2, '0');
  return `${dois(numero)}-de-${dois(total)}-${numeroProcesso}.pdf`;
}

/**
 * O completo pronto SEM as partes — gerado antes do formato ZIP. O core anterior à correção ainda
 * o devolvia ao pedido do ZIP, e o clique em "Baixar tudo (.zip)" dava 404: o que se baixa dele é
 * o PDF.
 *
 * @example prontoSemPartes({ ...pacote, status: 'PRONTO', partes: null }) // true
 */
export function prontoSemPartes(pacote: PacoteDosAutos | null): boolean {
  return pacote?.status === 'PRONTO' && !pacote.partes?.length;
}

/**
 * O formato em partes de fato entrega partes: foi pedido assim, e o pacote não chegou pronto sem
 * elas. Antes de pronto, o formato promete as partes.
 *
 * @example entregaEmPartes('partes', null) // true
 */
export function entregaEmPartes(formato: FormatoDownload, pacote: PacoteDosAutos | null): boolean {
  return formato === 'partes' && !prontoSemPartes(pacote);
}

/** As partes na ordem do documento, qualquer que seja a ordem em que vieram. */
export function partesEmOrdem(pacote: PacoteDosAutos): ParteDoPacote[] {
  return [...(pacote.partes ?? [])].sort((a, b) => a.numero - b.numero);
}

export function paginasDaParte(parte: ParteDoPacote): string {
  const ultima = parte.paginaInicial + parte.totalPaginas - 1;
  return parte.totalPaginas === 1
    ? `página ${parte.paginaInicial}`
    : `páginas ${parte.paginaInicial}–${ultima}`;
}

/**
 * O que o formato produz. Antes do pacote pronto o número de partes não existe — só o servidor
 * sabe onde cada uma fecha —, então o texto não inventa um.
 *
 * @example descricaoDosArquivos('partes', null) // 'Partes de até 5 MB'
 */
export function descricaoDosArquivos(
  formato: FormatoDownload,
  pacote: PacoteDosAutos | null,
): string {
  if (!entregaEmPartes(formato, pacote)) return '1 arquivo PDF';
  const total = pacote?.partes?.length ?? 0;
  if (total === 0) return `Partes de até ${LIMITE_POR_PARTE_MB} MB`;
  return `${total} ${total === 1 ? 'parte' : 'partes'} de até ${LIMITE_POR_PARTE_MB} MB`;
}

/** Tamanho real do que se baixa: o ZIP no formato em partes, o PDF nos demais. */
export function tamanhoDoDownload(formato: FormatoDownload, pacote: PacoteDosAutos): number | null {
  if (entregaEmPartes(formato, pacote)) return pacote.tamanhoZipBytes ?? pacote.tamanhoBytes;
  return pacote.tamanhoBytes;
}

/** O arquivo do botão principal do pacote pronto: o ZIP só quando há partes para zipar. */
export function arquivoPrincipalDo(
  formato: FormatoDownload | null,
  pacote: PacoteDosAutos | null,
): 'zip' | 'pdf' {
  return formato !== null && entregaEmPartes(formato, pacote) ? 'zip' : 'pdf';
}

/** Linha de resumo do pacote pronto; só o que o servidor mediu entra nela. */
export function resumoDoPacote(formato: FormatoDownload, pacote: PacoteDosAutos): string {
  const tamanho = tamanhoDoDownload(formato, pacote);
  return [
    pacote.totalPaginas !== null ? `${pacote.totalPaginas} páginas` : null,
    tamanho !== null ? formatarBytes(tamanho) : null,
    descricaoDosArquivos(formato, pacote),
  ]
    .filter((parte): parte is string => parte !== null)
    .join(' · ');
}
